import { SWRConfiguration } from "swr";

import { FetcherResult } from "../../../api-client/fetcher";
import { ModifiedSWRResponse } from "../../../api-client/useSwrGet";
import { useSearchTiresByPartNumber } from "../../../queries/useSearchTiresByPartNumber";
import { useSearchTiresByTiresize } from "../../../queries/useSearchTiresByTiresize";
import {
  FeaturedPartListItem,
  TireMetatags,
  TirePartItem,
  TireSearchResultsPayload,
  useSearchTiresByVehicle,
} from "../../../queries/useSearchTiresByVehicle";
import { useSearchTiresByVehicleChassis } from "../../../queries/useSearchTiresByVehicleChassis";
import { matchPartPricing } from "../../../util/matchPartPricing";
import { useTireCategories } from "./Filters/Category/useTireCategories";
import { useOriginalEquipment } from "./Filters/OriginalEquipment/useOriginalEquipment";
import { originalEquipmentOnly } from "./Filters/OriginalEquipment/useOriginalEquipmentOptions";
import { useRunFlat } from "./Filters/RunFlat/useRunFlat";
import { useTireBrands } from "./Filters/TireBrandFilters/useTireBrands";
import { useTireLoadRating } from "./Filters/TireLoadRaitingFilters/useTireLoadRating";
import { useTirePLYRating } from "./Filters/TirePlyRaitingFilters/useTirePLYRating";
import { dealerTirePriceFilters } from "./Filters/TirePriceFilters/useTirePriceOptions";
import { useTirePricing } from "./Filters/TirePriceFilters/useTirePricing";
import { useSpeedIndex } from "./Filters/TireSpeedIndexFilters/useSpeedIndex";

const mergeParts = (
  featuredPartList: FeaturedPartListItem[] = [],
  popularPartList: TirePartItem[] = [],
  partList: TirePartItem[] = []
): ModifiedTirePartItem[] => {
  const merged = new Map<string, ModifiedTirePartItem>();
  for (const { part } of featuredPartList) {
    merged.set(part.partDetails.primaryPart.itemId, {
      ...part,
      featured: true,
      popular: false,
    });
  }
  for (const part of popularPartList) {
    const key = part.partDetails.primaryPart.itemId;
    merged.set(key, { ...part, featured: merged.has(key), popular: true });
  }
  for (const part of partList) {
    const key = part.partDetails.primaryPart.itemId;
    if (!merged.has(key)) {
      merged.set(key, { ...part, featured: false, popular: false });
    }
  }
  return [...merged.values()];
};

export const useTireResults = (
  swrOptions: SWRConfiguration<FetcherResult<TireSearchResultsPayload>> = {}
): ModifiedSWRResponse<
  FetcherResult<TireSearchResultsPayload>,
  TireSearchResults | null
> => {
  const vehicleSearch = useSearchTiresByVehicle(swrOptions);
  const tireSizeSearch = useSearchTiresByTiresize(swrOptions);
  const partNumberSearch = useSearchTiresByPartNumber(swrOptions);
  const chassisSearch = useSearchTiresByVehicleChassis(swrOptions);

  const [brands] = useTireBrands();
  const [tags] = useTireCategories();
  const [pricing] = useTirePricing();
  const [tireLoadRating] = useTireLoadRating();
  const [plyRating] = useTirePLYRating();
  const [speedIndex] = useSpeedIndex();
  const [runFlat] = useRunFlat();
  const [originalEquipment] = useOriginalEquipment();

  const { data, ...rest } =
    [
      tireSizeSearch.data && tireSizeSearch,
      partNumberSearch.data && partNumberSearch,
      chassisSearch.data && chassisSearch,
    ].find(Boolean) || vehicleSearch;

  if (!data) {
    return { ...rest, data };
  }

  if (Array.isArray(data)) {
    return { ...rest, data: null };
  }

  const allParts = mergeParts(
    data.featuredPartList?.sort((a, b) => a.position - b.position),
    data.popularPartList,
    data.partList
  );

  const filteredParts = allParts.filter(
    ({ partDetails, partPricing, partSummary }) => {
      const brandsFilter =
        brands.length > 0
          ? brands.includes(partDetails.primaryPart.tireManufacturerName)
          : true;
      const pricingFiler =
        pricing.length > 0
          ? pricing
              .filter((index) => index in dealerTirePriceFilters)
              .some((index) => {
                const range = dealerTirePriceFilters[index];
                return matchPartPricing(partPricing, range);
              })
          : true;
      const tireLoadRatingFilter =
        tireLoadRating.length > 0
          ? tireLoadRating.includes(partDetails.primaryPart.tireLoadRating)
          : true;
      const plyRatingFilter =
        plyRating.length > 0
          ? plyRating.includes(partDetails.primaryPart.tirePlyRating)
          : true;
      const tireSpeedIndexFilter =
        speedIndex.length > 0
          ? speedIndex.includes(partDetails.primaryPart.tireSpeedIndex)
          : true;
      const runFlatFilter =
        runFlat.length > 0
          ? runFlat.includes(partDetails.primaryPart.tireRunFlat)
          : true;
      const originalEquipmentFilter =
        originalEquipment === originalEquipmentOnly
          ? Boolean(partDetails.primaryPart.tireOriginalEquipmentFlag)
          : true;
      const categoryFilter =
        tags.length > 0
          ? tags.some((tag) =>
              partDetails.primaryPart.tireCategoryTags.includes(tag)
            )
          : true;
      return (
        brandsFilter &&
        categoryFilter &&
        pricingFiler &&
        runFlatFilter &&
        tireLoadRatingFilter &&
        plyRatingFilter &&
        originalEquipmentFilter &&
        tireSpeedIndexFilter &&
        !partSummary.primaryPart.hideItem
      );
    }
  );

  return {
    ...rest,
    data: {
      ...data,
      allParts,
      filteredParts,
      metatagList: data.partListSummary.metatagList,
    },
  };
};

export type TireSearchResults = TireSearchResultsPayload & {
  allParts: ModifiedTirePartItem[];
  filteredParts: ModifiedTirePartItem[];
  metatagList: TireMetatags;
};

export type ModifiedTirePartItem = TirePartItem & {
  featured: boolean;
  popular: boolean;
};
